import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import LineCornerDownRightAltIcon from 'LuxKit/Icons/line/LineCornerDownRightAltIcon'
import type { MessagePart } from './messageTypes'
import {
  FLIGHTS_LANDING_PAGE_CONTEXT_LABEL,
  TRIP_PLANNER_ACTION_CONTEXT_LABEL,
  unescapeContextLabel,
  WHATSAPP_HANDOFF_CONTEXT_LABEL,
  WHATSAPP_NOTE_CONTEXT_LABEL,
} from 'tripPlanner/utils/contextTag'

const ContextLabel = styled.div`
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  font-size: ${rem(13)};
  max-width: 100%;
`

const ContextIcon = styled(LineCornerDownRightAltIcon)`
  vertical-align: text-bottom;
`

/**
 * Extracts the display label from the `label` attribute of a <Context> tag.
 */
function extractContextContent(text: string): string | undefined {
  const match = text.match(/<Context\s+label="([^"]*)"[^>]*>/i)
  return match?.[1] ? unescapeContextLabel(match[1]).trim() : undefined
}

const HIDDEN_CONTEXT_LABELS: ReadonlySet<string> = new Set([
  TRIP_PLANNER_ACTION_CONTEXT_LABEL,
  'LE Hotel room option',
  FLIGHTS_LANDING_PAGE_CONTEXT_LABEL,
  WHATSAPP_HANDOFF_CONTEXT_LABEL,
  WHATSAPP_NOTE_CONTEXT_LABEL,
])

/** The label of the message's first <Context> block, unless it is one the traveller never sees. */
export function getVisibleContextLabel(parts: Array<MessagePart>): string | undefined {
  const firstTextPart = parts.find(part => part.type === 'text')
  if (firstTextPart?.type !== 'text') return undefined
  const label = extractContextContent(firstTextPart.content)
  return label && !HIDDEN_CONTEXT_LABELS.has(label) ? label : undefined
}

interface Props {
  parts: Array<MessagePart>
  selectionLabels?: Array<string>
}

function MessageContextLabel({ parts, selectionLabels }: Props) {
  const intl = useIntl()
  const contextContent = useMemo(() => getVisibleContextLabel(parts), [parts])

  const localizedContextContent = useMemo(() => {
    switch (contextContent) {
      case 'Flight search result':
        return intl.formatMessage({
          id: 'KCWa94wmNeRBfCcJw8Ns',
          defaultMessage: 'Flight search result',
          description: 'Context label shown above a user message when it includes selected flight search results',
        })
      case 'Hotel search results':
        return intl.formatMessage({
          id: '8pZ5aEEqjXW9awoC2bsS',
          defaultMessage: 'Hotel search results',
          description: 'Context label shown above a user message when it includes hotel search results',
        })
      case 'LE Experience offer':
        return intl.formatMessage({
          id: 'M32MUJaIaQth7JlpGMmz',
          defaultMessage: 'LE Experience offer',
          description: 'Context label shown above a user message when it references a Luxury Escapes experience offer',
        })
      case 'LE Hotel offer':
        return intl.formatMessage({
          id: '4I3MQQpEbjiCdOrOKCet',
          defaultMessage: 'LE Hotel offer',
          description: 'Context label shown above a user message when it references a Luxury Escapes hotel offer',
        })
      default:
        return contextContent
    }
  }, [contextContent, intl])

  const selectionContent = useMemo(() => {
    if (!selectionLabels?.length) return undefined
    return selectionLabels.join(', ')
  }, [selectionLabels])

  const labelText = selectionContent ?? localizedContextContent

  if (!labelText) {
    return null
  }

  return (
    <ContextLabel>
      <ContextIcon size="S" /> {labelText}
    </ContextLabel>
  )
}

export default MessageContextLabel
